import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { gateEntryAPI, operationsAPI, orderAPI, dispatchAPI, salesPipelineAPI } from '../../services/api';

import {
  HiOutlineTruck, HiOutlineCog, HiOutlineClock, HiOutlineExclamation,
  HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineDocumentText
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import ApproveSalesOrderModal from '../../components/common/ApproveSalesOrderModal';
import Pagination from '../../components/common/Pagination';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { getReleasedBy, formatPaymentBasis, formatRole, formatDateTime } from '../../utils/salesOrderRelease';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;
// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://localhost:9898`;
const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

const AdminTrackerDashboard = () => {
  const { user } = useAuth();
  

  const [totalGateCount, setTotalGateCount] = useState(0);
  const [machines, setMachines] = useState([]);
  const [buildJobs, setBuildJobs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [qcLogs, setQcLogs] = useState([]);
  const [sbsLogs, setSbsLogs] = useState([]);
  const [orderShortages, setOrderShortages] = useState([]);
  const [dispatchStats, setDispatchStats] = useState({ assemble: 0, hold: 0, packing: 0, dispatched: 0 });
  const [pipelineStats, setPipelineStats] = useState({ overdue: 0, pendingApproval: 0 });
  const [pendingSalesOrders, setPendingSalesOrders] = useState([]);
  const [approvingOrder, setApprovingOrder] = useState(null);

  // Sales Order register: every SO with who released it, who approved it and where it is now
  const REGISTER_LIMIT = 10;
  const [registerOrders, setRegisterOrders] = useState([]);
  const [registerFilter, setRegisterFilter] = useState('');
  const [registerSearch, setRegisterSearch] = useState('');
  const [registerPage, setRegisterPage] = useState(1);
  const [registerMeta, setRegisterMeta] = useState({ total: 0, pages: 1 });
  
  const [loading, setLoading] = useState(true);


  const fetchTrackerData = useCallback(async () => {
    try {
      const [gateRes, machRes, buildRes, invRes, qcRes, sbsRes, shortRes, dispRes, pipeRes, pendingOrdersRes] = await Promise.all([
        gateEntryAPI.getAll({ limit: 10 }),
        operationsAPI.getMachines(),
        operationsAPI.getBuildJobs(),
        operationsAPI.getInventory(),
        operationsAPI.getQualityLogs(),
        operationsAPI.getShortageBuySales(),
        orderAPI.getShortages(),
        dispatchAPI.getStats().catch(() => ({ data: { data: {} } })),
        salesPipelineAPI.getStats().catch(() => ({ data: { data: {} } })),
        salesPipelineAPI.getAll({ stage: 'sales_order', approvalStatus: 'pending' }).catch(() => ({ data: { data: [] } }))
      ]);
      setTotalGateCount(gateRes.data.pagination?.total || gateRes.data.data.length);
      setMachines(machRes.data.data);
      setBuildJobs(buildRes.data.data);
      setInventory(invRes.data.data);
      setQcLogs(qcRes.data.data);
      setSbsLogs(sbsRes.data.data);
      setOrderShortages(shortRes.data.data);
      setDispatchStats(dispRes.data.data || {});
      setPipelineStats(pipeRes.data.data || {});
      setPendingSalesOrders(pendingOrdersRes.data.data || []);

    } catch (err) {
      toast.error('Failed to update dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRegister = useCallback(async () => {
    try {
      const params = { stage: 'sales_order', page: registerPage, limit: REGISTER_LIMIT };
      if (registerFilter) params.approvalStatus = registerFilter;
      if (registerSearch.trim()) params.search = registerSearch.trim();
      const res = await salesPipelineAPI.getAll(params);
      setRegisterOrders(res.data.data || []);
      setRegisterMeta({ total: res.data.total || 0, pages: res.data.pages || 1 });
    } catch (err) {
      setRegisterOrders([]);
    }
  }, [registerFilter, registerSearch, registerPage]);

  useEffect(() => { fetchRegister(); }, [fetchRegister]);

  const refreshAfterApproval = () => {
    fetchTrackerData();
    fetchRegister();
  };

  const handleApproveSalesOrder = (so) => setApprovingOrder(so);

  const handleRejectSalesOrder = async (id) => {
    const reason = window.prompt('Enter rejection reason (will be visible to Sales rep):');
    if (reason === null) return;
    try {
      await salesPipelineAPI.reject(id, reason.trim() || 'Pricing or Terms rejected by Admin');
      toast.error('❌ Sales Order Rejected');
      refreshAfterApproval();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject Sales Order');
    }
  };

  useEffect(() => {
    fetchTrackerData();

    let socket;
    if (user?.organizationId) {
      socket = io(SOCKET_URL, { withCredentials: true });
      socket.emit('join_org', user.organizationId);

      socket.on('gate_entry_updated', fetchTrackerData);
      socket.on('build_updated', fetchTrackerData);
      socket.on('inventory_updated', fetchTrackerData);
      socket.on('quality_updated', fetchTrackerData);
      socket.on('machine_updated', fetchTrackerData);
      socket.on('communication_updated', fetchTrackerData);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [fetchTrackerData, user?.organizationId]);

  // Aggregate Stats
  const activeMachines = machines.filter(m => m.status === 'working').length;
  const brokenMachines = machines.filter(m => m.status === 'broken').length;
  const totalStockCount = inventory.reduce((acc, curr) => acc + curr.quantity, 0);
  const activeBuildOrders = buildJobs.filter(j => ['pending', 'processing'].includes(j.status)).length;
  const shortageOrders = buildJobs.filter(j => j.status === 'shortage_reported').length;

  // Recharts Data Sets
  const registryVolumeData = [
    { name: 'Gate Logs', count: totalGateCount, color: '#3b82f6' },
    { name: 'Quality Audits', count: qcLogs.length, color: '#10b981' },
    { name: 'Production Runs', count: buildJobs.length, color: '#8b5cf6' },
    { name: 'Shortage Flags', count: sbsLogs.filter(l => l.type === 'shortage').length, color: '#ef4444' }
  ];

  const machineryStatusData = [
    { name: 'Working', value: machines.filter(m => m.status === 'working').length, color: '#10b981' },
    { name: 'Broken', value: machines.filter(m => m.status === 'broken').length, color: '#ef4444' },
    { name: 'Idle/Other', value: machines.filter(m => !['working', 'broken'].includes(m.status)).length, color: '#64748b' }
  ].filter(d => d.value > 0);

  const wipStagesData = [
    { name: 'Pending', value: buildJobs.filter(j => j.status === 'pending').length, color: '#eab308' },
    { name: 'Processing', value: buildJobs.filter(j => j.status === 'processing').length, color: '#3b82f6' },
    { name: 'Completed', value: buildJobs.filter(j => j.status === 'completed').length, color: '#22c55e' },
    { name: 'Shortages', value: buildJobs.filter(j => j.status === 'shortage_reported').length, color: '#ef4444' }
  ].filter(d => d.value > 0);

  const topInventoryData = [...inventory]
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 5)
    .map(item => ({
      name: item.name.length > 15 ? `${item.name.substring(0, 15)}...` : item.name,
      quantity: item.quantity
    }));

  const qcInspectionData = [
    { name: 'Inspected', value: qcLogs.filter(l => l.qcType === 'inspected').length, color: '#10b981' },
    { name: 'Damaged', value: qcLogs.filter(l => l.qcType === 'damaged').length, color: '#ef4444' },
    { name: 'Missed', value: qcLogs.filter(l => l.qcType === 'missed').length, color: '#f59e0b' }
  ].filter(d => d.value > 0);

  const sourcingStatusData = [
    { name: 'Pending', value: sbsLogs.filter(l => l.status === 'pending').length, color: '#ef4444' },
    { name: 'Assigned', value: sbsLogs.filter(l => l.status === 'assigned').length, color: '#3b82f6' },
    { name: 'Sourced', value: sbsLogs.filter(l => l.status === 'completed' || l.status === 'ordered').length, color: '#10b981' }
  ].filter(d => d.value > 0);

  return (
    <DashboardLayout pageTitle="Enterprise Real-Time Pipeline Tracker">
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} /> Initializing real-time analytics...
        </div>
      ) : (
        <>
          {/* Azure Status Cards */}
          <div className="stats-grid">
            <div className="stat-card blue">
              <div className="stat-header">
                <div className="stat-icon blue"><HiOutlineTruck /></div>
                <span className="azure-badge running">Live</span>
              </div>
              <div className="stat-value">{totalGateCount}</div>
              <div className="stat-label">Inward Gate Shipments Logs</div>
            </div>

            <div className="stat-card green">
              <div className="stat-header">
                <div className="stat-icon green"><HiOutlineCog /></div>
                <span style={{ fontSize: '0.75rem', color: 'var(--success)' }}>{activeMachines} Active / {brokenMachines} Repair</span>
              </div>
              <div className="stat-value">{machines.length}</div>
              <div className="stat-label">Factory Machinery Modules</div>
            </div>

            <div className="stat-card purple">
              <div className="stat-header">
                <div className="stat-icon purple"><HiOutlineClock /></div>
                <span className="azure-badge warning">{activeBuildOrders} In Line</span>
              </div>
              <div className="stat-value">{buildJobs.length}</div>
              <div className="stat-label">Production Build Jobs</div>
            </div>

            <div className="stat-card orange">
              <div className="stat-header">
                <div className="stat-icon orange"><HiOutlineExclamation /></div>
                <span className="azure-badge success" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                  ₹{inventory.reduce((acc, it) => acc + ((it.quantity || 0) * (it.unitPrice || 0)), 0).toLocaleString('en-IN')} Total Val
                </span>
              </div>
              <div className="stat-value">{totalStockCount}</div>
              <div className="stat-label">Unified Inventory Units Stocked</div>
            </div>


            <div className="stat-card blue">
              <div className="stat-header">
                <div className="stat-icon blue"><HiOutlineTruck /></div>
                <span className="azure-badge warning">{(dispatchStats.assemble || 0) + (dispatchStats.packing || 0)} In Progress</span>
              </div>
              <div className="stat-value">{(dispatchStats.assemble || 0) + (dispatchStats.packing || 0) + (dispatchStats.hold || 0)}</div>
              <div className="stat-label">Dispatch Orders Pending</div>
            </div>

            <div className="stat-card danger" style={{ borderLeft: '4px solid #ef4444' }}>
              <div className="stat-header">
                <div className="stat-icon red" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>🚨</div>
                <span className="azure-badge danger">Credit Overdue</span>
              </div>
              <div className="stat-value" style={{ color: '#ef4444' }}>{pipelineStats.overdue || 0}</div>
              <div className="stat-label">Payment Due / Overdue Accounts</div>
            </div>

            <div className="stat-card warning" style={{ borderLeft: '4px solid #eab308' }}>
              <div className="stat-header">
                <div className="stat-icon yellow" style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308' }}>⏳</div>
                <span className="azure-badge warning">Action Required</span>
              </div>
              <div className="stat-value" style={{ color: '#eab308' }}>{pipelineStats.pendingApproval || pendingSalesOrders.length || 0}</div>
              <div className="stat-label">Sales Orders Awaiting Approval</div>
            </div>
          </div>




          {/* Detailed Lists Grids replaced by Recharts Graphical Components */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr', gap: '28px', marginBottom: '28px' }}>
            
            {/* WIP Stages Donut Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Production WIP Stages Distribution</h3>
              <div style={{ width: '100%', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {wipStagesData.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>No active production runs.</div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={wipStagesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {wipStagesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Operations Registry Volume Bar Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Operations Registry Volume Chart</h3>
              <div style={{ width: '100%', height: '260px', flexGrow: 1 }}>
                <ResponsiveContainer>
                  <BarChart data={registryVolumeData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                    <XAxis dataKey="name" fontSize={11} stroke="var(--text-dim)" tickLine={false} />
                    <YAxis fontSize={11} stroke="var(--text-dim)" tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(249, 115, 22, 0.04)' }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {registryVolumeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '28px', marginBottom: '28px' }}>
            
            {/* Machinery Live Status Donut Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Machinery Health & Status</h3>
              <div style={{ width: '100%', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {machineryStatusData.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>No machinery modules registered.</div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={machineryStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {machineryStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Inventory Levels Bar Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Top Inventory Stock Snapshot</h3>
              <div style={{ width: '100%', height: '260px', flexGrow: 1 }}>
                {topInventoryData.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>No inventory data available.</div>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={topInventoryData} layout="vertical" margin={{ top: 10, right: 10, left: 30, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                      <XAxis type="number" fontSize={11} stroke="var(--text-dim)" tickLine={false} />
                      <YAxis dataKey="name" type="category" fontSize={11} stroke="var(--text-dim)" tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Bar dataKey="quantity" fill="#f97316" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr', gap: '28px' }}>
            
            {/* Quality control results Pie Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Quality Inspections Audit Outcomes</h3>
              <div style={{ width: '100%', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {qcInspectionData.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>No quality inspections logged.</div>
                ) : (
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={qcInspectionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {qcInspectionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Shortage & Sourcing Status Bar Chart */}
            <div className="azure-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1rem', fontWeight: 700 }}>Shortage & Sourcing Sourcing Registry</h3>
              <div style={{ width: '100%', height: '260px', flexGrow: 1 }}>
                {sourcingStatusData.length === 0 ? (
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>No shortages or sourcing logs.</div>
                ) : (
                  <ResponsiveContainer>
                    <BarChart data={sourcingStatusData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                      <XAxis dataKey="name" fontSize={11} stroke="var(--text-dim)" tickLine={false} />
                      <YAxis fontSize={11} stroke="var(--text-dim)" tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: 'rgba(249, 115, 22, 0.04)' }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {sourcingStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

          </div>

          {/* Sales Orders Awaiting Admin Approval Panel */}
          <div className="azure-card" style={{ padding: '24px', marginTop: '28px', border: pendingSalesOrders.length > 0 ? '1px solid rgba(234, 179, 8, 0.4)' : '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                🚨 Sales Orders Awaiting Admin Approval
                {pendingSalesOrders.length > 0 && (
                  <span className="azure-badge warning">{pendingSalesOrders.length} Pending</span>
                )}
              </h3>
            </div>

            {pendingSalesOrders.length === 0 ? (
              <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>✅ All Sales Orders have been reviewed and approved!</p>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>Customer & Rep</th>
                      <th>SO Ref #</th>
                      <th>Order Items / Specs</th>
                      <th>Grand Total</th>
                      <th>Terms Proposed by Sales</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingSalesOrders.map(so => {
                      const cfg = so.items?.[0]?.dragChainConfig || so.dragChainConfig || {};
                      return (
                        <tr key={so._id}>
                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{so.customerName}</div>
                            {(() => {
                              const released = getReleasedBy(so);
                              return released.name ? (
                                <div style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>
                                  👤 Released by: {released.name}{released.role ? ` (${formatRole(released.role)})` : ''}
                                  {released.at && <div style={{ color: 'var(--text-dim)', fontWeight: 400 }}>{formatDateTime(released.at)}</div>}
                                </div>
                              ) : null;
                            })()}
                            {so.salesPersonName && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Rep: {so.salesPersonName}</div>
                            )}
                            {so.customerContact && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>📞 {so.customerContact}</div>
                            )}
                          </td>
                          <td style={{ fontWeight: 600, color: 'var(--primary)' }}>
                            {so.salesOrderNumber || so.customerPoNumber || so.quotationNumber || 'SO-NEW'}
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            {(cfg.modelNumber || cfg.series) ? (
                              <div>
                                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{cfg.modelNumber || cfg.series}</span>
                                <div style={{ color: 'var(--text-dim)', fontSize: '0.78rem' }}>
                                  {[cfg.width && `W: ${cfg.width}`, cfg.radius && `R: ${cfg.radius}`, cfg.numberOfLinks && `${cfg.numberOfLinks} links`].filter(Boolean).join(' | ')}
                                </div>
                                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary)' }}>
                                  Qty: {cfg.quantity || 1} m
                                </div>
                              </div>
                            ) : so.items && so.items.length > 0 ? (
                              <div>
                                {so.items.map((it, idx) => (
                                  <div key={idx} style={{ fontSize: '0.8rem' }}>
                                    <strong>{it.modelNumber}</strong> - {it.quantity} {it.unit || 'pcs'}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-dim)' }}>--</span>
                            )}
                          </td>
                          <td style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                            ₹{(so.grandTotal || so.totalAmount || 0).toLocaleString('en-IN')}
                          </td>
                          <td style={{ fontSize: '0.82rem' }}>
                            <div>{so.paymentTerms || (so.creditDays ? `${so.creditDays} days` : 'Immediate')}</div>
                            {so.creditDueDate && (
                              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>
                                Due: {new Date(so.creditDueDate).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button
                                onClick={() => handleApproveSalesOrder(so)}
                                className="btn btn-sm"
                                style={{ background: '#22c55e', color: '#fff', border: 'none', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                title="Approve Sales Order"
                              >
                                <HiOutlineCheckCircle /> Approve
                              </button>
                              <button
                                onClick={() => handleRejectSalesOrder(so._id)}
                                className="btn btn-sm"
                                style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                title="Reject Sales Order"
                              >
                                <HiOutlineXCircle /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sales Order Register — who released each order, who approved it, and where it is now */}
          <div className="azure-card" style={{ padding: '24px', marginTop: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                📋 Sales Order Register
              </h3>
              <input
                className="form-input"
                style={{ maxWidth: '260px' }}
                placeholder="Search SO # / customer"
                value={registerSearch}
                onChange={e => { setRegisterSearch(e.target.value); setRegisterPage(1); }}
              />
            </div>
            <TabBar style={{ marginBottom: '14px' }}>
              {[['', 'All'], ['pending', 'Awaiting Approval'], ['approved', 'Approved'], ['rejected', 'Rejected']].map(([key, label]) => (
                <TabBtn key={key || 'all'} active={registerFilter === key} onClick={() => { setRegisterFilter(key); setRegisterPage(1); }}>
                  {label}
                </TabBtn>
              ))}
            </TabBar>

            {registerOrders.length === 0 ? (
              <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No Sales Orders found.</p>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>SO # & Customer</th>
                      <th>Order Value</th>
                      <th>Released By</th>
                      <th>Admin Approval</th>
                      <th>Payment Basis</th>
                      <th>Store Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registerOrders.map(so => {
                      const released = getReleasedBy(so);
                      const status = so.approvalStatus || 'pending';
                      const badge = status === 'approved' ? 'success' : status === 'rejected' ? 'danger' : 'warning';
                      const job = so.dispatchId && typeof so.dispatchId === 'object' ? so.dispatchId : null;
                      return (
                        <tr key={so._id}>
                          <td>
                            <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{so.salesOrderNumber || '—'}</div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{so.customerName}</div>
                          </td>
                          <td style={{ fontWeight: 700 }}>₹{(so.grandTotal || so.totalAmount || 0).toLocaleString('en-IN')}</td>
                          <td style={{ fontSize: '0.84rem' }}>
                            <div style={{ fontWeight: 600 }}>{released.name || '—'}</div>
                            {released.role && <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem', textTransform: 'capitalize' }}>{formatRole(released.role)}</div>}
                            {released.at && <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{formatDateTime(released.at)}</div>}
                          </td>
                          <td style={{ fontSize: '0.84rem' }}>
                            <span className={`azure-badge ${badge}`}>{status.toUpperCase()}</span>
                            {status !== 'pending' && so.approvedBy?.name && (
                              <div style={{ marginTop: '4px' }}>by {so.approvedBy.name}</div>
                            )}
                            {status !== 'pending' && so.approvalDate && (
                              <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>{formatDateTime(so.approvalDate)}</div>
                            )}
                            {status === 'rejected' && so.rejectionReason && (
                              <div style={{ color: 'var(--danger)', fontSize: '0.75rem' }}>{so.rejectionReason}</div>
                            )}
                            {status === 'pending' && (
                              <button className="btn btn-primary btn-sm" style={{ marginTop: '6px' }} onClick={() => handleApproveSalesOrder(so)}>Review</button>
                            )}
                          </td>
                          <td style={{ fontSize: '0.84rem', fontWeight: 600 }}>
                            {formatPaymentBasis(so) || <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>Not decided</span>}
                          </td>
                          <td style={{ fontSize: '0.84rem' }}>
                            {job ? (
                              <>
                                <span className={`azure-badge ${job.status === 'hold' ? 'warning' : job.status === 'dispatched' ? 'success' : 'running'}`}>
                                  {(job.status || '').toUpperCase()}
                                </span>
                                {job.status === 'hold' && job.holdReason && (
                                  <div style={{ color: 'var(--text-dim)', fontSize: '0.75rem', marginTop: '4px' }}>{job.holdReason}</div>
                                )}
                              </>
                            ) : (
                              <span style={{ color: 'var(--text-dim)' }}>Not sent to store</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Pagination
                  currentPage={registerPage}
                  totalPages={registerMeta.pages}
                  totalItems={registerMeta.total}
                  itemsPerPage={REGISTER_LIMIT}
                  onPageChange={setRegisterPage}
                />
              </div>
            )}
          </div>

          {/* Client Order Shortages (Difference Reports) */}
          <div className="azure-card" style={{ padding: '24px', marginTop: '28px' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚠️ Client Order Shortages (Difference Reports)
            </h3>
            {orderShortages.length === 0 ? (
              <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No active client order shortages.</p>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>Ref Order</th>
                      <th>Model Number</th>
                      <th>Shortage Qty</th>
                      <th>Status</th>
                      <th>Planned Shots</th>
                      <th>Date Flagged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderShortages.map(s => (
                      <tr key={s._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.orderId?.orderNumber || 'N/A'}</td>
                        <td style={{ fontWeight: 600 }}>{s.modelNumber}</td>
                        <td style={{ fontWeight: 700, color: 'var(--danger)' }}>{s.shortageMeters}m</td>
                        <td>
                          <span className={`azure-badge ${s.status === 'pending_planning' ? 'danger' : 'success'}`}>
                            {s.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td>{s.plannedShots > 0 ? `${s.plannedShots} shots` : 'Not Planned'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{new Date(s.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {approvingOrder && (
        <ApproveSalesOrderModal
          order={approvingOrder}
          onClose={() => setApprovingOrder(null)}
          onApproved={refreshAfterApproval}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminTrackerDashboard;
